<template>
  <nav class="sidebar-nav">
    <div v-if="loading" class="loading-section">
      <div class="spinner"></div>
      <span>Loading menu...</span>
    </div>
    
    <div v-else-if="error" class="error-section">
      <p class="error-message">{{ error }}</p>
      <button @click="retryFetch" class="retry-button">Retry</button>
    </div>
    
    <div v-else>
      <!-- Handle the filtered and organized menu structure -->
      <div v-for="(section, sectionName) in organizedMenuItems" :key="sectionName" class="menu-section">
        <h3 class="section-title">{{ sectionName }}</h3>
        
        <div 
          v-for="header in section" 
          :key="header.id" 
          class="menu-group"
          :class="{ 'expanded': expandedGroups[sectionName] || searchActive }"
        >
          <!-- Header Item (Category) -->
          <div 
            class="menu-header" 
            @click="toggleGroup(sectionName)"
            :draggable="isCustomizing ? true : false"
            @dragstart="isCustomizing ? startDrag($event, sectionName) : null"
            @dragover.prevent
            @drop="isCustomizing ? onDrop($event, sectionName) : null"
            v-show="!searchQuery || header.children.some(item => matchesSearch(item)) || matchesSearch(header)"
          >
            <div class="header-content">
              <span class="header-icon" v-if="header.icon">
                <i :class="header.icon"></i>
              </span>
              <span class="header-title">{{ header.title }}</span>
              <span v-if="!isCollapsed" class="expander">
                <i class="fas" :class="expandedGroups[sectionName] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              </span>
            </div>
          </div>
          
          <!-- Child Items -->
          <div v-if="(expandedGroups[sectionName] || searchActive) && !isCollapsed" class="menu-items">
            <div 
              v-for="item in header.children" 
              :key="item.id" 
              class="menu-item"
              :class="{ 'active': isActiveRoute(item.url), 'search-match': searchQuery && matchesSearch(item) }"
              v-show="!searchQuery || matchesSearch(item)"
            >
              <router-link 
                :to="item.url" 
                class="menu-link"
                @contextmenu="showQuickActions(convertToMenuItem(item), $event)"
              >
                <span class="item-icon" v-if="item.icon">
                  <i :class="item.icon"></i>
                </span>
                <span class="item-title" v-if="!isCollapsed">{{ item.title }}</span>
                <span v-if="item.badge" class="badge" :class="`badge-${item.badge_color || 'primary'}`">
                  {{ item.badge }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Simplified collapsed view for mobile/collapsed state -->
      <div v-if="isCollapsed" class="collapsed-menu">
        <div 
          v-for="section in getAllSectionItems()" 
          :key="section.id"
          class="collapsed-item"
          :class="{ 'active': isActiveRoute(section.url) }"
          v-show="!searchQuery || matchesSearch(section)"
        >
          <router-link :to="section.url" class="collapsed-link" :title="section.title">
            <span class="collapsed-icon">
              <i :class="section.icon || 'bi bi-circle'"></i>
            </span>
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { useRoute } from 'vue-router';

export default {
  name: 'SidebarNavigation',
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    },
    sortedGroups: {
      type: Array,
      default: () => []
    },
    expandedGroups: {
      type: Object,
      default: () => ({})
    },
    searchQuery: {
      type: String,
      default: ''
    },
    isCustomizing: {
      type: Boolean,
      default: false
    },
    organizedMenuItems: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    searchActive: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-group', 'show-quick-actions', 'start-drag', 'drop', 'retry-fetch'],
  setup(props, { emit }) {
    const route = useRoute();
    
    // Helper to check if current route matches a menu item
    const isActiveRoute = (url) => {
      if (!url) return false;
      return route.path === url;
    };
    
    // Flatten all menu items for collapsed view
    const getAllSectionItems = () => {
      const items = [];
      
      // Only return non-header items with URLs
      for (const section in props.organizedMenuItems) {
        for (const header of props.organizedMenuItems[section]) {
          if (header.children && header.children.length) {
            for (const item of header.children) {
              if (item.url) {
                items.push(item);
              }
            }
          }
        }
      }
      
      return items;
    };
    
    // Helper to check if item matches search query
    const matchesSearch = (item) => {
      if (!props.searchQuery) return true;
      
      const query = props.searchQuery.toLowerCase();
      const title = (item.title || '').toLowerCase();
      const url = (item.url || '').toLowerCase();
      
      return title.includes(query) || url.includes(query);
    };
    
    // Convert API item to menu item format for quick actions
    const convertToMenuItem = (item) => {
      return {
        title: item.title,
        path: item.url || '#',
        icon: item.icon || 'circle'
      };
    };
    
    // Handler for retrying failed fetches
    const retryFetch = () => {
      emit('retry-fetch');
    };
    
    // Event handlers that pass events up to parent
    const toggleGroup = (groupName) => {
      emit('toggle-group', groupName);
    };
    
    const showQuickActions = (item, event) => {
      emit('show-quick-actions', item, event);
    };
    
    const startDrag = (event, index) => {
      emit('start-drag', event, index);
    };
    
    const onDrop = (event, targetIndex) => {
      emit('drop', event, targetIndex);
    };
    
    return {
      isActiveRoute,
      toggleGroup,
      showQuickActions,
      startDrag,
      onDrop,
      getAllSectionItems,
      convertToMenuItem,
      retryFetch,
      matchesSearch
    };
  }
};
</script>

<style scoped>
.sidebar-nav {
  margin-top: 1rem;
}

.section-title {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.4);
  padding: 0.5rem 0;
  margin: 0.5rem 0;
}

.menu-group {
  margin-bottom: 0.5rem;
}

.menu-header {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  user-select: none;
  border-radius: 0.375rem;
  transition: background 0.2s;
}

.menu-header[draggable="true"] {
  cursor: grab;
  border-left: 3px solid #4f46e5;
  padding-left: 0.5rem;
  background: rgba(79, 70, 229, 0.08);
}

.menu-header[draggable="true"]:active {
  cursor: grabbing;
}

.menu-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
  background-color: transparent;
}

.header-icon {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.header-title {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
}

.expander {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
  transition: transform 0.3s ease;
}

.menu-group.expanded .expander {
  transform: rotate(0deg);
}

.menu-items {
  padding-left: 1rem;
  margin-top: 0.5rem;
}

.menu-item {
  margin: 0.3rem 0;
  position: relative;
}

.menu-item.search-match .menu-link {
  background: rgba(79, 70, 229, 0.15);
  color: #efefef;
  border-color: rgba(79, 70, 229, 0.3);
}

.menu-link {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.8rem;
  border-radius: 0.375rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: all 0.2s;
  font-size: 0.875rem;
  border: 1px solid transparent;
}

.menu-link:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.menu-item.active .menu-link {
  background: rgba(79, 70, 229, 0.1);
  color: white;
  border-color: rgba(79, 70, 229, 0.2);
}

.item-icon {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.menu-item.active .item-icon {
  color: #4f46e5;
}

.menu-item.search-match .item-icon {
  color: #4f46e5;
}

.item-title {
  flex: 1;
}

.badge {
  padding: 0.15rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.625rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-primary {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
}

.badge-success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.badge-warning {
  background: rgba(234, 179, 8, 0.1);
  color: #eab308;
}

.badge-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.collapsed-menu {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.collapsed-item {
  width: 100%;
  display: flex;
  justify-content: center;
}

.collapsed-link {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: all 0.2s;
}

.collapsed-link:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.collapsed-item.active .collapsed-link {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
}

.collapsed-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-section, 
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
  text-align: center;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spinner 0.8s linear infinite;
  margin-bottom: 1rem;
}

.error-message {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.retry-button {
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
  border: 1px solid rgba(79, 70, 229, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background: rgba(79, 70, 229, 0.15);
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

/* Light theme overrides */
:root[data-theme="light"] .section-title {
  color: rgba(0, 0, 0, 0.4);
}

:root[data-theme="light"] .header-content {
  background-color: transparent;
}

:root[data-theme="light"] .menu-header {
  color: #4b5563;
}

:root[data-theme="light"] .menu-header:hover {
  background: rgba(0, 0, 0, 0.03);
}

:root[data-theme="light"] .menu-header[draggable="true"] {
  border-left-color: #4f46e5;
  background: rgba(79, 70, 229, 0.05);
}

:root[data-theme="light"] .header-icon {
  color: #6b7280;
}

:root[data-theme="light"] .expander {
  color: rgba(0, 0, 0, 0.3);
}

:root[data-theme="light"] .menu-link {
  color: #4b5563;
}

:root[data-theme="light"] .menu-link:hover {
  background: rgba(0, 0, 0, 0.03);
  color: #1f2937;
}

:root[data-theme="light"] .menu-item.active .menu-link {
  background: rgba(79, 70, 229, 0.05);
  color: #1f2937;
}

:root[data-theme="light"] .menu-item.search-match .menu-link {
  background: rgba(79, 70, 229, 0.1);
  color: #1f2937;
  border-color: rgba(79, 70, 229, 0.2);
}

:root[data-theme="light"] .item-icon {
  color: #6b7280;
}

:root[data-theme="light"] .collapsed-link {
  color: #4b5563;
}

:root[data-theme="light"] .collapsed-link:hover {
  background: rgba(0, 0, 0, 0.03);
  color: #1f2937;
}

:root[data-theme="light"] .error-message {
  color: #4b5563;
}

:root[data-theme="light"] .spinner {
  border-color: rgba(0, 0, 0, 0.1);
  border-top-color: #4f46e5;
}
</style> 